import { styled } from '@mui/material/styles';
import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { nameofFactory } from 'src/utils/nameofFactory';
import { useFormContext } from 'react-hook-form';
import { SelectChangeEvent, Box } from '@mui/material';
import { useGetDomainMetaData } from 'src/use-cases/invoice/useInvoices';
import { useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MultipleSelectDropdown } from '../analytics/filter/MultipleSelectDropdown';
import { dateReferenceOptions } from '../analytics/filter/MockData';
import { FilterLoader } from './Loader';

const StyledBox = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '180px',
    [theme.breakpoints.down(1520)]: {
      width: '140px',
    },
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
  },
  '& .MuiPaper-root': {
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
    border: '1px solid #DFDFDF',
  },
  [theme.breakpoints.down(1520)]: {
    '& .MuiInputBase-input': {
      fontSize: '14px',
    },
  },
}));

const StyledBox2 = styled(Box)(({ theme }) => ({
  '& .MuiFormControl-root': {
    width: '210px',
    [theme.breakpoints.down(1520)]: {
      width: '160px',
    },
  },
  '& .MuiInputBase-root': {
    borderRadius: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '16px',
  },
  '& .MuiPaper-root': {
    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09)',
    border: '1px solid #DFDFDF',
  },
  [theme.breakpoints.down(1520)]: {
    '& .MuiInputBase-input': {
      fontSize: '14px',
    },
    '& .MuiFormLabel-root': {
      fontSize: '14px',
    },
  },
}));

const nameof = nameofFactory<AnalyticSearchRequestDto>();

export const CurrencyReferenceFilter = ({ options, control, onChange = null, defaultValue }) => (
  <StyledBox>
    <MultipleSelectDropdown
      name='currencyReference'
      label='Currency Reference'
      control={control}
      defaultValue={defaultValue}
      options={options}
      multiple={false}
      onChangeCustom={onChange}
    />
  </StyledBox>
);

export const DateReferenceFilter = ({ control, onChange = null, defaultValue = 'PICKUP_DATE' }) => (
  <StyledBox>
    <MultipleSelectDropdown
      name={nameof('dateReference')}
      label='Date Reference'
      control={control}
      defaultValue='PICKUP_DATE'
      options={dateReferenceOptions}
      multiple={false}
      onChangeCustom={onChange}
    />
  </StyledBox>
);

export const InvoiceStatusFilter = ({ options, control, onChange = null, defaultValue = ['APPROVED'] }) => (
  <StyledBox2>
    <MultipleSelectDropdown
      name={nameof('invoiceStatusSet')}
      label='Invoice Status'
      control={control}
      defaultValue={defaultValue}
      options={options}
      onChangeCustom={onChange}
      withApplyButton={true}
    />
  </StyledBox2>
);

interface IFilterHeaderProps {
  onChangeFilter?(filter: Record<string, any>): void;
  isDashboard?: boolean;
  allowedCurrencies: string[];
}

export const FilterHeader = ({ onChangeFilter, isDashboard, allowedCurrencies }: IFilterHeaderProps) => {
  const { control, watch, setValue } = useFormContext();
  const formValues = watch();
  const [searchParams] = useSearchParams();

  const { data: dataStatus, isLoading: isLoadingStatus } = useGetDomainMetaData();
  const globalCurrency = dataStatus?.defaultCurrency;

  const currencyList = useMemo(() => {
    const items = [];
    if (allowedCurrencies && allowedCurrencies.length > 0) {
      // Sort currencies to prioritize global currency if it exists
      const sortedCurrencies = [...allowedCurrencies].sort((a, b) => {
        if (globalCurrency) {
          if (a === globalCurrency) return -1;
          if (b === globalCurrency) return 1;
        }
        return a.localeCompare(b);
      });

      sortedCurrencies.forEach((item) => {
        items.push({
          label: item,
          value: item,
        });
      });
    } else if (globalCurrency) {
      items.push({
        label: globalCurrency,
        value: globalCurrency,
      });
    }

    return items;
  }, [allowedCurrencies, globalCurrency]);
  const statusList = useMemo(() => {
    const items = [];
    if (!isDashboard && dataStatus?.resolvedInvoiceStatuses && dataStatus?.resolvedInvoiceStatuses?.length > 0) {
      items.push({
        label: 'RESOLVED',
        value: '',
        isLabel: true,
      });

      dataStatus?.resolvedInvoiceStatuses?.forEach((item) => {
        items.push({
          label: item === 'APPROVED' ? 'Ready for Payment' : item?.replace('_', ' ')?.toLowerCase(),
          value: item,
          isLabel: false,
        });
      });
    }
    if (dataStatus?.unResolvedInvoiceStatuses && dataStatus?.unResolvedInvoiceStatuses?.length > 0) {
      items.push({
        label: 'UNRESOLVED',
        value: '',
        isLabel: true,
      });

      dataStatus?.unResolvedInvoiceStatuses?.forEach((item) => {
        items.push({
          label: item?.replace('_', ' ')?.toLowerCase(),
          value: item,
          isLabel: false,
        });
      });
    }

    return items;
  }, [dataStatus, isDashboard]);

  // Effect to set initial currency if not already set
  useEffect(() => {
    if (currencyList.length > 0 && !formValues?.currencyReference && !searchParams.get('currencyReference')) {
      setValue('currencyReference', currencyList[0]?.value, { shouldDirty: false });
    }
  }, [currencyList, setValue, searchParams, formValues?.currencyReference]);

  // Get the current currency value or default to first in list
  const currentCurrency = formValues?.currencyReference || searchParams.get('currencyReference') || currencyList[0]?.value;

  // Effect to set form values from URL parameters on component mount
  useEffect(() => {
    const currencyRef = searchParams.get('currencyReference');
    const dateRef = searchParams.get('dateReference');
    const statusSet = searchParams.get('invoiceStatusSet');

    if (currencyRef) {
      setValue('currencyReference', currencyRef);
    }

    if (dateRef) {
      setValue('dateReference', dateRef);
    }

    if (statusSet) {
      const statusValues = statusSet.split(',');
      setValue('invoiceStatusSet', statusValues);
    }
  }, [searchParams, setValue]);

  return (
    <div className='flex items-center gap-12 xl:gap-16'>
      <div className='flex items-center gap-12 xl:gap-16'>
        {isLoadingStatus ? (
          <FilterLoader />
        ) : (
          <CurrencyReferenceFilter
            key={`currency-${currentCurrency}`}
            control={control}
            options={currencyList}
            onChange={(event: SelectChangeEvent<string | string[]>) => {
              onChangeFilter({
                ...formValues,
                currencyReference: event.target.value,
              });
            }}
            defaultValue={currentCurrency}
          />
        )}
        {isLoadingStatus ? (
          <FilterLoader />
        ) : (
          <DateReferenceFilter
            control={control}
            onChange={(event: SelectChangeEvent<string | string[]>) => {
              onChangeFilter({
                ...formValues,
                dateReference: event.target.value,
              });
            }}
            defaultValue={formValues?.dateReference || searchParams.get('dateReference') || 'PICKUP_DATE'}
          />
        )}
        <div className='w-1 h-16 bg-neutral-20' />
        {!isDashboard &&
          (isLoadingStatus ? (
            <FilterLoader />
          ) : (
            <InvoiceStatusFilter
              control={control}
              options={statusList}
              onChange={(event: SelectChangeEvent<string | string[]>) => {
                if (onChangeFilter) {
                  onChangeFilter({
                    ...formValues,
                    invoiceStatusSet: event.target.value,
                  });
                }
              }}
              defaultValue={
                formValues?.invoiceStatusSet || (searchParams.get('invoiceStatusSet') ? searchParams.get('invoiceStatusSet')?.split(',') : ['APPROVED'])
              }
            />
          ))}
      </div>
    </div>
  );
};
